import React, { useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import jaLocale from '@fullcalendar/core/locales/ja';
import { Box, CircularProgress } from '@mui/material';
import { categoryColors, categoryBorderColors, badgeColors, publishingStatusColors, mediaStatusColors } from '../../utils/colors';
import { groupContentsByTheme } from '../../services/s3Service';

const MAX_VISIBLE_EVENTS = 5;

// 掲載状態の優先順位（高い方が優先：降版済 > 掲載済み > 出稿済み > 未掲載）
const PUBLISHING_STATUS_PRIORITY = {
  '降版済み': 4,
  '掲載済み': 3,
  '出稿済み': 2,
  '未掲載': 1,
};

// 派生状態の優先順位（高い方が優先：派生済 > 未派生）
const MEDIA_STATUS_PRIORITY = {
  '派生済': 2,
  '未派生': 1,
};

// テーマ内の掲載状態を集約（最も優先度の高い状態を1つ返す）
const aggregatePublishingStatus = (contents) => {
  let best = '';
  let bestPriority = 0;
  contents.forEach((c) => {
    const status = (c.publishingStatus || '').replace(/\s/g, '');
    const priority = PUBLISHING_STATUS_PRIORITY[status] || 0;
    if (priority > bestPriority) {
      bestPriority = priority;
      best = status;
    }
  });
  return best;
};

// テーマ内の派生状態を集約（最も優先度の高い状態を1つ返す）
const aggregateMediaStatus = (contents) => {
  let best = '';
  let bestPriority = 0;
  contents.forEach((c) => {
    const status = (c.mediaStatus || '').replace(/\s/g, '');
    const priority = MEDIA_STATUS_PRIORITY[status] || 0;
    if (priority > bestPriority) {
      bestPriority = priority;
      best = status;
    }
  });
  return best;
};

const MemoCalendarMain = ({ isMobile, selectedDate, onDateSelect, onShowMore, calendarRef, filteredContents, loading }) => {
  const [currentView, setCurrentView] = useState('dayGridMonth');

  // テーマ単位にグループ化
  const themeGroups = useMemo(() => {
    return groupContentsByTheme(filteredContents);
  }, [filteredContents]);

  // 日付ごとにテーマをグループ化
  const themesByDate = useMemo(() => {
    const byDate = {};
    themeGroups.forEach((theme, index) => {
      const dateKey = theme.dateKey;
      if (!byDate[dateKey]) {
        byDate[dateKey] = [];
      }
      byDate[dateKey].push({ ...theme, themeIndex: index });
    });
    return byDate;
  }, [themeGroups]);

  // イベントクリック時のハンドラー
  const handleEventClick = (info) => {
    const isMoreButton = info.event.extendedProps?.isMoreButton;
    const dateKey = info.event.extendedProps?.dateKey;

    if (isMoreButton) {
      const dateThemes = themesByDate[dateKey] || [];
      if (onShowMore) {
        onShowMore(new Date(dateKey), dateThemes);
      }
      return;
    }

    const themeIndex = info.event.extendedProps?.themeIndex;
    if (themeIndex !== undefined) {
      const theme = themeGroups[themeIndex];
      if (theme) {
        onDateSelect(new Date(theme.dateKey), theme);
      }
    }
  };

  // 月表示のみ件数制限をかける
  const isMonthView = currentView === 'dayGridMonth';

  // カレンダーに表示するイベントを作成
  const events = [];
  Object.keys(themesByDate).forEach(dateKey => {
    const themes = themesByDate[dateKey];
    const visibleThemes = isMonthView ? themes.slice(0, MAX_VISIBLE_EVENTS) : themes;
    const remainingCount = isMonthView ? themes.length - MAX_VISIBLE_EVENTS : 0;

    visibleThemes.forEach((theme, index) => {
      const genre = theme.newsGenre || '';
      const pubStatus = aggregatePublishingStatus(theme.contents);
      const mediaStatus = aggregateMediaStatus(theme.contents);

      // 紐づく記事の種類別枚数を集計
      const memoKindCounts = { photo: 0, eps: 0, pdf: 0 };
      theme.contents.forEach(c => {
        const kind = c.memoKind || '';
        if (kind === '写真') memoKindCounts.photo++;
        else if (kind === 'EPS' || kind === 'ＥＰＳ') memoKindCounts.eps++;
        else if (kind === 'PDF' || kind === 'ＰＤＦ') memoKindCounts.pdf++;
      });

      events.push({
        id: `theme-${dateKey}-${index}`,
        title: theme.themeName || '(テーマ名なし)',
        start: dateKey,
        allDay: true,
        backgroundColor: categoryColors[genre] || '#b0bec5',
        borderColor: categoryBorderColors[genre] || '#78909c',
        order: index,
        extendedProps: {
          themeIndex: theme.themeIndex,
          dateKey: dateKey,
          themeName: theme.themeName || '(テーマ名なし)',
          newsGenre: genre,
          publishingStatus: pubStatus,
          mediaStatus: mediaStatus,
          firstCreatedDate: theme.firstCreatedDate || '',
          memoKindCounts,
        }
      });
    });

    // 月表示で残りがある場合のみ「+N件」ボタン
    if (remainingCount > 0) {
      events.push({
        id: `more-${dateKey}`,
        title: `+${remainingCount}件`,
        start: dateKey,
        allDay: true,
        backgroundColor: '#1976d2',
        borderColor: '#1565c0',
        order: 9999,
        extendedProps: {
          isMoreButton: true,
          dateKey: dateKey,
          remainingCount: remainingCount,
        }
      });
    }
  });

  // チップスタイル
  const chipStyle = {
    display: 'inline-block',
    fontSize: '0.7rem',
    lineHeight: 1.2,
    padding: '2px 6px',
    borderRadius: '3px',
    color: '#fff',
    marginRight: '2px',
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
  };

  // カスタムイベントレンダリング
  const renderEventContent = (eventInfo) => {
    const { extendedProps } = eventInfo.event;

    // 「+N件」ボタンの場合
    if (extendedProps?.isMoreButton) {
      if (isMobile && (currentView === 'dayGridMonth' || currentView === 'dayGridWeek')) {
        return (
          <div style={{
            height: '18px',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            textAlign: 'center',
            lineHeight: '18px',
            color: '#fff',
          }}>
            +{extendedProps.remainingCount}
          </div>
        );
      }
      return (
        <div style={{
          padding: '8px',
          fontSize: '0.875rem',
          fontWeight: 'bold',
          textAlign: 'center',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}>
          {eventInfo.event.title}
        </div>
      );
    }

    // スマホ月・週表示はシンプルなバー表示
    if (isMobile && (currentView === 'dayGridMonth' || currentView === 'dayGridWeek')) {
      return (
        <div style={{ width: '100%', height: '18px' }} />
      );
    }

    const newsGenre = extendedProps?.newsGenre;
    const publishingStatus = extendedProps?.publishingStatus;
    const mediaStatus = extendedProps?.mediaStatus;
    const themeName = extendedProps?.themeName || eventInfo.event.title;
    const firstCreatedDate = extendedProps?.firstCreatedDate || '';
    const memoKindCounts = extendedProps?.memoKindCounts || {};

    return (
      <div style={{
        padding: '6px 8px',
        overflow: 'hidden',
        width: '100%',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.3,
        fontSize: '0.85rem',
        fontWeight: 500,
        color: '#000000',
      }}>
        {/* バッジ行 */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '2px',
          marginBottom: '4px',
        }}>
          {newsGenre && (
            <span style={{ ...chipStyle, backgroundColor: badgeColors.newsGenre }}>
              {newsGenre}
            </span>
          )}
          {publishingStatus && (
            <span style={{ ...chipStyle, backgroundColor: publishingStatusColors[publishingStatus] || badgeColors.publishingStatus }}>
              {publishingStatus.charAt(0)}
            </span>
          )}
          {mediaStatus && mediaStatus !== '未派生' && (
            <span style={{ ...chipStyle, backgroundColor: mediaStatusColors[mediaStatus] || badgeColors.mediaStatus }}>
              {mediaStatus.charAt(0)}
            </span>
          )}
        </div>
        {/* テーマ名（最大20文字） */}
        <div style={{
          overflow: 'hidden',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          marginBottom: '2px',
        }}>
          {themeName.length > 20 ? themeName.slice(0, 20) + '…' : themeName}
        </div>
        {/* 記事種類別枚数 */}
        {(memoKindCounts.photo > 0 || memoKindCounts.eps > 0 || memoKindCounts.pdf > 0) && (
          <div style={{
            display: 'flex',
            gap: '4px',
            fontSize: '0.65rem',
            marginBottom: '2px',
          }}>
            {memoKindCounts.photo > 0 && (
              <span style={{ ...chipStyle, backgroundColor: '#1e88e5', fontSize: '0.65rem', padding: '1px 4px' }}>
                写真:{memoKindCounts.photo}
              </span>
            )}
            {memoKindCounts.eps > 0 && (
              <span style={{ ...chipStyle, backgroundColor: '#43a047', fontSize: '0.65rem', padding: '1px 4px' }}>
                EPS:{memoKindCounts.eps}
              </span>
            )}
            {memoKindCounts.pdf > 0 && (
              <span style={{ ...chipStyle, backgroundColor: '#e53935', fontSize: '0.65rem', padding: '1px 4px' }}>
                PDF:{memoKindCounts.pdf}
              </span>
            )}
          </div>
        )}
        {/* 作成日時 */}
        {firstCreatedDate && (
          <div style={{
            fontSize: '0.7rem',
            color: '#555',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}>
            作成:{firstCreatedDate}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        maxWidth: 1680,
        width: '100%',
        mx: 'auto',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
        initialView={sessionStorage.getItem('memoCalendarView') || (isMobile ? 'listWeek' : 'dayGridMonth')}
        initialDate={sessionStorage.getItem('calendarDate') || undefined}
        events={events}
        locale={jaLocale}
        eventClick={handleEventClick}
        eventContent={renderEventContent}
        selectable={true}
        headerToolbar={isMobile
          ? { left: '', center: 'title', right: '' }
          : { left: 'prev,next today', center: 'title', right: 'dayGridMonth,dayGridWeek,dayGridDay,listWeek' }
        }
        eventMinHeight={46}
        dayMaxEventRows={false}
        eventDisplay="block"
        eventOrder="order"
        datesSet={(info) => {
          setCurrentView(info.view.type);
          sessionStorage.setItem('memoCalendarView', info.view.type);
          sessionStorage.setItem('calendarDate', info.view.currentStart.toISOString());
        }}
        dayHeaderContent={(args) => {
          if (isMobile && args.view?.type === 'dayGridWeek') {
            const date = new Date(args.date);
            return date.getDate();
          }
          if (isMobile && args.view?.type.startsWith('list')) {
            const date = new Date(args.date);
            const options = { weekday: 'short', day: 'numeric' };
            return date.toLocaleDateString('ja-JP', options);
          }
          return args.text;
        }}
      />
    </Box>
  );
};

export default MemoCalendarMain;
